import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import theme from '../../styles/theme';
import {
  getDorySyncLists,
  runInitialJournalSyncToDory,
  convertFirebaseTimestamp
} from '../../services/funnelService';

const Container = styled.div`
  background: white;
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.xl};
  box-shadow: ${theme.shadows.sm};
  margin-bottom: ${theme.spacing.lg};
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: ${theme.spacing.md};
  margin-bottom: ${theme.spacing.lg};
  flex-wrap: wrap;
`;

const HeaderText = styled.div`
  flex: 1;
  min-width: 200px;
`;

const SectionTitle = styled.h2`
  font-family: ${theme.typography.headerFont};
  font-size: ${theme.typography.sizes.h2};
  font-weight: ${theme.typography.weights.semiBold};
  color: ${theme.colors.text};
  margin: 0 0 ${theme.spacing.sm} 0;
`;

const Subtitle = styled.p`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
  color: ${theme.colors.textLight};
  margin: 0;
`;

const ListSection = styled.div`
  margin-bottom: ${theme.spacing.xl};

  &:last-child {
    margin-bottom: 0;
  }
`;

const ListTitle = styled.h3`
  font-family: ${theme.typography.headerFont};
  font-size: ${theme.typography.sizes.h3};
  font-weight: ${theme.typography.weights.semiBold};
  color: ${theme.colors.text};
  margin: 0 0 ${theme.spacing.sm} 0;
`;

const Button = styled.button`
  background: ${props => props.variant === 'primary'
    ? `linear-gradient(135deg, ${theme.colors.primary} 0%, ${theme.colors.accent} 100%)`
    : 'white'};
  color: ${props => props.variant === 'primary' ? 'white' : theme.colors.text};
  border: ${props => props.variant === 'primary' ? 'none' : `2px solid ${theme.colors.primary}`};
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
  font-weight: ${theme.typography.weights.medium};
  cursor: pointer;
  transition: all ${theme.animation.durations.fast};
  white-space: nowrap;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: ${theme.shadows.md};
  }

  &:disabled {
    background: ${theme.colors.disabled};
    border-color: ${theme.colors.disabled};
    color: white;
    cursor: not-allowed;
    transform: none;
  }
`;

const ErrorMessage = styled.div`
  background: ${theme.colors.error}20;
  border: 1px solid ${theme.colors.error};
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing.md};
  margin-bottom: ${theme.spacing.lg};
  color: ${theme.colors.error};
  font-family: ${theme.typography.bodyFont};
`;

const SuccessMessage = styled.div`
  background: ${theme.colors.success}20;
  border: 1px solid ${theme.colors.success};
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing.md};
  margin-bottom: ${theme.spacing.lg};
  color: ${theme.colors.success};
  font-family: ${theme.typography.bodyFont};
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: ${theme.spacing.xl};
  color: ${theme.colors.textLight};
  font-family: ${theme.typography.bodyFont};
`;

const EmptyMessage = styled.div`
  text-align: center;
  padding: ${theme.spacing.lg};
  color: ${theme.colors.textLight};
  font-family: ${theme.typography.bodyFont};
`;

const CountBadge = styled.span`
  display: block;
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.small};
  color: ${theme.colors.textLight};
  margin-bottom: ${theme.spacing.sm};
`;

const TableWrapper = styled.div`
  overflow-x: auto;
  margin-bottom: ${theme.spacing.lg};
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
`;

const Th = styled.th`
  text-align: left;
  padding: ${theme.spacing.md};
  border-bottom: 2px solid ${theme.colors.doryLighterBlue};
  color: ${theme.colors.textLight};
  font-weight: ${theme.typography.weights.medium};
  font-size: ${theme.typography.sizes.small};
  white-space: nowrap;
`;

const Tr = styled.tr`
  cursor: ${props => props.$selectable ? 'pointer' : 'default'};
  background: ${props => props.$selected ? `${theme.colors.primary}12` : 'transparent'};
  border-bottom: 1px solid ${theme.colors.doryLighterBlue};

  &:hover {
    background: ${props => {
      if (props.$selected) return `${theme.colors.primary}18`;
      if (props.$selectable) return `${theme.colors.neutral}08`;
      return 'transparent';
    }};
  }
`;

const Td = styled.td`
  padding: ${theme.spacing.md};
  color: ${theme.colors.text};
  vertical-align: middle;
`;

const Radio = styled.input`
  width: 16px;
  height: 16px;
  cursor: pointer;
  accent-color: ${theme.colors.primary};
`;

const Mono = styled.span`
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: ${theme.typography.sizes.small};
`;

const Actions = styled.div`
  display: flex;
  gap: ${theme.spacing.md};
  align-items: center;
  flex-wrap: wrap;
`;

const SelectedHint = styled.span`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.small};
  color: ${theme.colors.textLight};
`;

const formatLastActive = (lastActive) => {
  if (!lastActive) return '—';
  const date = convertFirebaseTimestamp(lastActive) || new Date(lastActive);
  if (!date || isNaN(date.getTime())) return '—';
  return date.toLocaleString();
};

const SyncScreen = () => {
  const [pendingUsers, setPendingUsers] = useState([]);
  const [syncedUsers, setSyncedUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedShortcode, setSelectedShortcode] = useState(null);

  const loadUsers = useCallback(async ({ clearMessages = true } = {}) => {
    setLoading(true);
    if (clearMessages) {
      setError('');
      setSuccess('');
    }
    try {
      const { pending, synced } = await getDorySyncLists();
      setPendingUsers(pending);
      setSyncedUsers(synced);
      setSelectedShortcode((current) => {
        if (current && pending.some((u) => u.shortcode === current)) {
          return current;
        }
        return null;
      });
    } catch (err) {
      console.error('Failed to load sync users:', err);
      setError(err.message || 'Failed to load sync users');
      setPendingUsers([]);
      setSyncedUsers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const selectedUser = pendingUsers.find((u) => u.shortcode === selectedShortcode) || null;

  const handleSync = async () => {
    if (!selectedUser) {
      setError('Select a user to sync');
      return;
    }

    setSyncing(true);
    setError('');
    setSuccess('');

    try {
      const result = await runInitialJournalSyncToDory({
        shortcode: selectedUser.shortcode,
        appUserId: selectedUser.appUserId,
        entries: selectedUser.journalEntries || []
      });

      setSuccess(
        `Synced ${selectedUser.shortcode}: ${result.createdCount} journal entr${result.createdCount === 1 ? 'y' : 'ies'} sent to Dory (synced ${result.syncedDate}).`
      );
      setSelectedShortcode(null);
      await loadUsers({ clearMessages: false });
    } catch (err) {
      console.error('Failed to sync user to Dory:', err);
      const message = err?.message || err?.details || 'Failed to sync user to Dory';
      setError(message);
    } finally {
      setSyncing(false);
    }
  };

  return (
    <Container>
      <SectionHeader>
        <HeaderText>
          <SectionTitle>Sync</SectionTitle>
          <Subtitle>
            Manage initial Dory journal syncs for Insatiable users with an appUserId.
          </Subtitle>
        </HeaderText>
        <Button onClick={() => loadUsers()} disabled={loading || syncing}>
          Refresh
        </Button>
      </SectionHeader>

      {error && <ErrorMessage>{error}</ErrorMessage>}
      {success && <SuccessMessage>{success}</SuccessMessage>}

      {loading ? (
        <LoadingMessage>Loading sync users…</LoadingMessage>
      ) : (
        <>
          <ListSection>
            <ListTitle>Ready to sync</ListTitle>
            {pendingUsers.length === 0 ? (
              <EmptyMessage>No users are waiting for an initial sync.</EmptyMessage>
            ) : (
              <>
                <CountBadge>
                  {pendingUsers.length} user{pendingUsers.length === 1 ? '' : 's'} ready
                </CountBadge>
                <TableWrapper>
                  <Table>
                    <thead>
                      <tr>
                        <Th aria-label="Select" />
                        <Th>Shortcode</Th>
                        <Th>Name</Th>
                        <Th>Email</Th>
                        <Th>appUserId</Th>
                        <Th>Journal entries</Th>
                        <Th>Last active</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {pendingUsers.map((user) => {
                        const isSelected = user.shortcode === selectedShortcode;
                        return (
                          <Tr
                            key={user.shortcode}
                            $selected={isSelected}
                            $selectable
                            onClick={() => setSelectedShortcode(user.shortcode)}
                          >
                            <Td>
                              <Radio
                                type="radio"
                                name="sync-user"
                                checked={isSelected}
                                onChange={() => setSelectedShortcode(user.shortcode)}
                                onClick={(e) => e.stopPropagation()}
                              />
                            </Td>
                            <Td><Mono>{user.shortcode}</Mono></Td>
                            <Td>{user.userInfo?.name || '—'}</Td>
                            <Td>{user.userInfo?.email || '—'}</Td>
                            <Td><Mono>{user.appUserId}</Mono></Td>
                            <Td>{user.journalEntryCount}</Td>
                            <Td>{formatLastActive(user.lastActive)}</Td>
                          </Tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </TableWrapper>

                <Actions>
                  <Button
                    variant="primary"
                    onClick={handleSync}
                    disabled={!selectedUser || syncing}
                  >
                    {syncing ? 'Syncing…' : 'Sync'}
                  </Button>
                  {selectedUser ? (
                    <SelectedHint>
                      Sync all {selectedUser.journalEntryCount} journal entr{selectedUser.journalEntryCount === 1 ? 'y' : 'ies'} for {selectedUser.shortcode}
                    </SelectedHint>
                  ) : (
                    <SelectedHint>Select a user, then tap Sync</SelectedHint>
                  )}
                </Actions>
              </>
            )}
          </ListSection>

          <ListSection>
            <ListTitle>Already synced</ListTitle>
            {syncedUsers.length === 0 ? (
              <EmptyMessage>No users have been synced yet.</EmptyMessage>
            ) : (
              <>
                <CountBadge>
                  {syncedUsers.length} user{syncedUsers.length === 1 ? '' : 's'} synced
                </CountBadge>
                <TableWrapper>
                  <Table>
                    <thead>
                      <tr>
                        <Th>Shortcode</Th>
                        <Th>Name</Th>
                        <Th>Email</Th>
                        <Th>appUserId</Th>
                        <Th>Journal entries</Th>
                        <Th>Last active</Th>
                        <Th>Date of sync</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {syncedUsers.map((user) => (
                        <Tr key={user.shortcode}>
                          <Td><Mono>{user.shortcode}</Mono></Td>
                          <Td>{user.userInfo?.name || '—'}</Td>
                          <Td>{user.userInfo?.email || '—'}</Td>
                          <Td><Mono>{user.appUserId}</Mono></Td>
                          <Td>{user.journalEntryCount}</Td>
                          <Td>{formatLastActive(user.lastActive)}</Td>
                          <Td>{user.synced || '—'}</Td>
                        </Tr>
                      ))}
                    </tbody>
                  </Table>
                </TableWrapper>
              </>
            )}
          </ListSection>
        </>
      )}
    </Container>
  );
};

export default SyncScreen;
